import type { Breadcrumb, BrowserOptions } from "@sentry/react";

type SentrySpanJson = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];

/**
 * Query parameters that make a URL a credential: a CloudFront signed URL
 * (`Signature`, `Policy`, `Key-Pair-Id`) or an S3 presigned one
 * (`X-Amz-Signature`, `X-Amz-Credential`). Anyone holding the URL can use it
 * until it expires.
 */
const SIGNING_PARAM = /[?&](?:signature|policy|key-pair-id|x-amz-signature|x-amz-credential)=/i;

/**
 * A signed URL as Sentry may keep it: the origin alone (NG-3276). The query string
 * is the signature, and the path of an upload names the customer's file. Any other
 * URL comes back unchanged.
 */
export function redactSignedUrl(url: string): string {
  if (!SIGNING_PARAM.test(url)) return url;
  const origin = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]+/i.exec(url)?.[0];
  return origin ? `${origin}/[signed]` : "[signed]";
}

/** Every URL inside a string (a span description is `PUT https://…`). */
function redactSignedUrlsIn(text: string): string {
  return text.replace(/[a-z][a-z0-9+.-]*:\/\/\S+/gi, (url) => redactSignedUrl(url));
}

/**
 * The xhr / fetch breadcrumb Sentry records for every request carries its full
 * URL, and it rides along on the next event captured, an upload failure included.
 */
export function redactSignedBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const url = breadcrumb.data?.url;
  if (typeof url !== "string" || !SIGNING_PARAM.test(url)) return breadcrumb;
  return { ...breadcrumb, data: { ...breadcrumb.data, url: redactSignedUrl(url) } };
}

/**
 * The browser's `http.client` span for a storage request. API spans are already
 * rewritten to their route by `prepareApiHealthSpan`; a third-party span is left as
 * the SDK built it, so a signed upload's URL sat in its description and data.
 */
export function redactSignedSpan(span: SentrySpanJson): SentrySpanJson {
  if (span.op !== "http.client") return span;
  const signed =
    SIGNING_PARAM.test(span.description ?? "") ||
    Object.values(span.data ?? {}).some(
      (value) => typeof value === "string" && SIGNING_PARAM.test(value),
    );
  if (!signed) return span;
  const data: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(span.data ?? {})) {
    if (/query|fragment/i.test(key)) continue;
    data[key] = typeof value === "string" ? redactSignedUrlsIn(value) : value;
  }
  return {
    ...span,
    description: span.description ? redactSignedUrlsIn(span.description) : span.description,
    data: data as SentrySpanJson["data"],
  };
}
